import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import type {
  AdminGlobalSettings,
  AdminStats,
  AdminTrendPoint,
  AdminUserSummary,
  AdminUsersPage,
  AdminUserUpdate,
} from "../types";
import "../admin.css";

type AdminSection = "overview" | "users" | "settings";

interface AdminPageProps {
  currentUserId: number;
  stats: AdminStats | null;
  users: AdminUsersPage | null;
  settings: AdminGlobalSettings | null;
  refreshStats: () => Promise<AdminStats | null>;
  loadUsers: (search?: string, page?: number, pageSize?: number) => Promise<AdminUsersPage | null>;
  updateUser: (userId: number, update: AdminUserUpdate) => Promise<AdminUserSummary>;
  loadSettings: () => Promise<AdminGlobalSettings | null>;
  saveSettings: (settings: AdminGlobalSettings) => Promise<AdminGlobalSettings>;
  onError: (message: string) => void;
}

export function AdminPage({
  currentUserId,
  stats,
  users,
  settings,
  refreshStats,
  loadUsers,
  updateUser,
  loadSettings,
  saveSettings,
  onError,
}: AdminPageProps) {
  const [section, setSection] = useState<AdminSection>("overview");
  const [query, setQuery] = useState("");
  const [busyUser, setBusyUser] = useState<number | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [draft, setDraft] = useState<AdminGlobalSettings | null>(settings);

  useEffect(() => setDraft(settings), [settings]);

  async function run(action: () => Promise<unknown>, fallback: string) {
    try {
      return await action();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : fallback);
      return null;
    }
  }

  async function refreshCurrent() {
    setRefreshing(true);
    try {
      if (section === "overview") await run(refreshStats, "Unable to refresh business metrics.");
      if (section === "users") await run(
        () => loadUsers(query, users?.page || 1, users?.page_size || 25),
        "Unable to refresh users.",
      );
      if (section === "settings") await run(loadSettings, "Unable to refresh global settings.");
    } finally {
      setRefreshing(false);
    }
  }

  async function searchUsers(event: FormEvent) {
    event.preventDefault();
    await run(() => loadUsers(query, 1, users?.page_size || 25), "Unable to search users.");
  }

  async function changeUser(user: AdminUserSummary, update: AdminUserUpdate) {
    if (update.is_admin === false && user.is_admin) {
      const confirmed = window.confirm(`Remove administrator access from ${user.email}?`);
      if (!confirmed) return;
    }
    setBusyUser(user.id);
    try {
      const changed = await run(
        () => updateUser(user.id, update),
        "Unable to update this user.",
      );
      if (changed) await refreshStats();
    } finally {
      setBusyUser(null);
    }
  }

  async function submitSettings(event: FormEvent) {
    event.preventDefault();
    if (!draft) return;
    setSavingSettings(true);
    try {
      const saved = await run(() => saveSettings(draft), "Unable to save global settings.");
      if (saved) {
        setDraft(saved as AdminGlobalSettings);
        await refreshStats();
      }
    } finally {
      setSavingSettings(false);
    }
  }

  return (
    <section className="admin-console">
      <header className="admin-console-header">
        <div>
          <p className="eyebrow">FITTRACKER OPERATIONS</p>
          <h1>Business dashboard</h1>
          <p>Monitor growth, manage customers, and control platform-wide product policy.</p>
        </div>
        <button className="button admin-refresh" type="button" onClick={() => void refreshCurrent()} disabled={refreshing}>
          <Icon name="refresh" /> {refreshing ? "Refreshing…" : "Refresh data"}
        </button>
      </header>

      <nav className="admin-tabs" aria-label="Admin console sections">
        <Tab active={section === "overview"} label="Overview" detail="KPIs and trends" onClick={() => setSection("overview")} />
        <Tab active={section === "users"} label="Users" detail={users ? `${users.total.toLocaleString()} accounts` : "Customer access"} onClick={() => setSection("users")} />
        <Tab active={section === "settings"} label="Global settings" detail="Platform policy" onClick={() => setSection("settings")} />
      </nav>

      {section === "overview" ? <Overview stats={stats} onLoad={() => void run(refreshStats, "Unable to load business metrics.")} /> : null}
      {section === "users" ? (
        <UsersPanel
          currentUserId={currentUserId}
          users={users}
          query={query}
          busyUser={busyUser}
          onQuery={setQuery}
          onSearch={searchUsers}
          onPage={(page) => void run(() => loadUsers(query, page, users?.page_size || 25), "Unable to load users.")}
          onChange={(user, update) => void changeUser(user, update)}
        />
      ) : null}
      {section === "settings" ? (
        <SettingsPanel
          stats={stats}
          draft={draft}
          saving={savingSettings}
          onChange={setDraft}
          onSubmit={submitSettings}
          onReset={() => setDraft(settings)}
        />
      ) : null}
    </section>
  );
}

function Tab({ active, label, detail, onClick }: { active: boolean; label: string; detail: string; onClick: () => void }) {
  return (
    <button className={active ? "active" : ""} type="button" onClick={onClick}>
      <strong>{label}</strong><span>{detail}</span>
    </button>
  );
}

function Overview({ stats, onLoad }: { stats: AdminStats | null; onLoad: () => void }) {
  if (!stats) {
    return <section className="admin-empty"><h2>Business data is not available.</h2><button className="button primary" type="button" onClick={onLoad}>Load dashboard</button></section>;
  }

  const premiumRate = stats.total_users ? stats.premium_users / stats.total_users * 100 : 0;
  const activationRate = stats.total_users ? stats.active_users_30d / stats.total_users * 100 : 0;

  return (
    <div className="admin-section-stack">
      <div className="admin-kpis">
        <Kpi label="Total users" value={stats.total_users} detail={`+${stats.new_users_30d} in 30 days`} />
        <Kpi label="Active users" value={stats.active_users_30d} detail={`${activationRate.toFixed(1)}% monthly reach`} />
        <Kpi label="Premium users" value={stats.premium_users} detail={`${premiumRate.toFixed(1)}% conversion`} />
        <Kpi label="Plans generated" value={stats.plans_30d} detail={`${stats.total_plans.toLocaleString()} all time`} />
        <Kpi label="Completed workouts" value={stats.completed_workouts_30d} detail={`${stats.total_workouts.toLocaleString()} tracked`} />
        <Kpi label="Nutrition logs" value={stats.total_meals} detail={`${stats.total_events.toLocaleString()} platform events`} />
      </div>

      <div className="admin-chart-grid">
        <section className="admin-panel admin-trend-panel">
          <PanelHeading eyebrow="30 DAY TREND" title="Growth and engagement" detail="Daily active users, new registrations, and generated plans." />
          <TrendChart points={stats.trend_30d} />
        </section>
        <section className="admin-panel">
          <PanelHeading eyebrow="CUSTOMER MIX" title="Account composition" detail="Current premium penetration and administrator count." />
          <UserMix stats={stats} />
        </section>
      </div>

      <div className="admin-chart-grid lower">
        <section className="admin-panel">
          <PanelHeading eyebrow="ENGAGEMENT" title="Top user actions" detail="Most frequent events across the platform." />
          <BarList values={stats.events_by_type} empty="No events have been recorded." />
        </section>
        <section className="admin-panel">
          <PanelHeading eyebrow="PLAN DEMAND" title="Why plans are generated" detail="Generation volume grouped by product trigger." />
          <BarList values={stats.plans_by_trigger} empty="No plans have been generated." />
        </section>
        <section className="admin-panel">
          <PanelHeading eyebrow="INTEGRATIONS" title="Wearable adoption" detail={`${stats.wearable_users.toLocaleString()} users have at least one connection.`} />
          <BarList values={stats.connected_wearables} empty="No wearable accounts are connected." />
        </section>
      </div>

      <section className="admin-runtime-strip">
        <RuntimeFlag label="AI generation" value={stats.mock_mode ? "Mock mode" : "Live Claude"} warning={stats.mock_mode} />
        <RuntimeFlag label="Premium enforcement" value={stats.enforce_premium ? "Enabled" : "Disabled"} />
        <RuntimeFlag label="Plan authentication" value={stats.require_auth_for_plans ? "Required" : "Optional"} />
        <RuntimeFlag label="Administrators" value={stats.admin_users.toLocaleString()} />
      </section>
    </div>
  );
}

function Kpi({ label, value, detail }: { label: string; value: number; detail: string }) {
  return <section className="admin-kpi"><span>{label}</span><strong>{value.toLocaleString()}</strong><small>{detail}</small></section>;
}

function PanelHeading({ eyebrow, title, detail }: { eyebrow: string; title: string; detail: string }) {
  return <header className="admin-panel-heading"><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><span>{detail}</span></header>;
}

function TrendChart({ points }: { points: AdminTrendPoint[] }) {
  const width = 760;
  const height = 250;
  const padding = 28;
  const max = Math.max(...points.flatMap((point) => [point.active_users, point.new_users, point.plans]), 1);
  const coordinates = (key: "active_users" | "new_users" | "plans") => points.map((point, index) => {
    const x = padding + index * ((width - padding * 2) / Math.max(points.length - 1, 1));
    const y = height - padding - point[key] / max * (height - padding * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <div className="admin-trend-chart">
      <div className="admin-chart-legend"><span className="active-users">Active users</span><span className="new-users">New users</span><span className="plans">Plans</span></div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Thirty day growth and engagement chart">
        {[0, 1, 2, 3, 4].map((line) => {
          const y = padding + line * ((height - padding * 2) / 4);
          return <line key={line} x1={padding} x2={width - padding} y1={y} y2={y} className="grid-line" />;
        })}
        <polyline points={coordinates("active_users")} className="trend-line active-users" />
        <polyline points={coordinates("new_users")} className="trend-line new-users" />
        <polyline points={coordinates("plans")} className="trend-line plans" />
      </svg>
      <div className="admin-chart-axis"><span>{formatShortDate(points[0]?.date)}</span><span>{formatShortDate(points[Math.floor(points.length / 2)]?.date)}</span><span>{formatShortDate(points.at(-1)?.date)}</span></div>
    </div>
  );
}

function UserMix({ stats }: { stats: AdminStats }) {
  const premium = stats.total_users ? stats.premium_users / stats.total_users * 100 : 0;
  const style = { "--premium-share": `${premium}%` } as CSSProperties;
  return (
    <div className="admin-user-mix">
      <div className="admin-donut" style={style}><div><strong>{premium.toFixed(1)}%</strong><span>premium</span></div></div>
      <dl>
        <div><dt><i className="premium" />Premium</dt><dd>{stats.premium_users.toLocaleString()}</dd></div>
        <div><dt><i className="free" />Free</dt><dd>{Math.max(0, stats.total_users - stats.premium_users).toLocaleString()}</dd></div>
        <div><dt><i className="admin" />Administrators</dt><dd>{stats.admin_users.toLocaleString()}</dd></div>
      </dl>
    </div>
  );
}

function BarList({ values, empty }: { values: Record<string, number>; empty: string }) {
  const entries = Object.entries(values).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const max = Math.max(...entries.map(([, value]) => value), 1);
  if (!entries.length) return <p className="admin-muted">{empty}</p>;
  return (
    <div className="admin-bars">
      {entries.map(([label, value]) => (
        <div key={label} className="admin-bar-row">
          <div><span>{humanize(label)}</span><strong>{value.toLocaleString()}</strong></div>
          <div className="admin-bar-track"><i style={{ width: `${Math.max(3, value / max * 100)}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function RuntimeFlag({ label, value, warning = false }: { label: string; value: string; warning?: boolean }) {
  return <div><span>{label}</span><strong className={warning ? "warning" : ""}>{value}</strong></div>;
}

interface UsersPanelProps {
  currentUserId: number;
  users: AdminUsersPage | null;
  query: string;
  busyUser: number | null;
  onQuery: (value: string) => void;
  onSearch: (event: FormEvent) => void;
  onPage: (page: number) => void;
  onChange: (user: AdminUserSummary, update: AdminUserUpdate) => void;
}

function UsersPanel({ currentUserId, users, query, busyUser, onQuery, onSearch, onPage, onChange }: UsersPanelProps) {
  const pages = users ? Math.max(1, Math.ceil(users.total / users.page_size)) : 1;
  return (
    <section className="admin-panel admin-users-panel">
      <div className="admin-users-toolbar">
        <PanelHeading eyebrow="CUSTOMERS" title="User management" detail="Search every account and control premium or administrator access." />
        <form onSubmit={onSearch} className="admin-user-search">
          <input value={query} onChange={(event) => onQuery(event.target.value)} placeholder="Search name or email" aria-label="Search users" />
          <button className="button admin-refresh" type="submit">Search</button>
        </form>
      </div>

      {users ? (
        <>
          <div className="admin-table-wrap">
            <table className="admin-users-table">
              <thead><tr><th>User</th><th>Joined</th><th>Last active</th><th>Usage</th><th>Premium</th><th>Administrator</th></tr></thead>
              <tbody>{users.items.map((user) => (
                <tr key={user.id}>
                  <td><div className="admin-user-identity"><span>{(user.name || user.email).slice(0, 1).toUpperCase()}</span><div><strong>{user.name || "Unnamed user"}</strong><small>{user.email}</small></div></div></td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>{user.last_active_at ? formatDate(user.last_active_at) : "Never"}</td>
                  <td><div className="admin-usage"><span>{user.total_plans} plans</span><span>{user.total_workouts} workouts</span><span>{user.wearable_connections} wearables</span></div></td>
                  <td><ToggleButton active={user.is_premium} disabled={busyUser === user.id} onClick={() => onChange(user, { is_premium: !user.is_premium })} activeLabel="Premium" inactiveLabel="Free" /></td>
                  <td><ToggleButton active={user.is_admin} disabled={busyUser === user.id || (user.id === currentUserId && user.is_admin)} onClick={() => onChange(user, { is_admin: !user.is_admin })} activeLabel="Admin" inactiveLabel="User" /></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <footer className="admin-pagination"><span>Showing {users.items.length} of {users.total.toLocaleString()} accounts</span><div><button type="button" disabled={users.page <= 1} onClick={() => onPage(users.page - 1)}>Previous</button><span>Page {users.page} of {pages}</span><button type="button" disabled={users.page >= pages} onClick={() => onPage(users.page + 1)}>Next</button></div></footer>
        </>
      ) : <div className="admin-empty"><p>No users loaded.</p></div>}
    </section>
  );
}

function ToggleButton({ active, disabled, onClick, activeLabel, inactiveLabel }: { active: boolean; disabled: boolean; onClick: () => void; activeLabel: string; inactiveLabel: string }) {
  return <button className={`admin-toggle ${active ? "active" : ""}`} type="button" disabled={disabled} onClick={onClick}><i />{active ? activeLabel : inactiveLabel}</button>;
}

interface SettingsPanelProps {
  stats: AdminStats | null;
  draft: AdminGlobalSettings | null;
  saving: boolean;
  onChange: (value: AdminGlobalSettings) => void;
  onSubmit: (event: FormEvent) => void;
  onReset: () => void;
}

function SettingsPanel({ stats, draft, saving, onChange, onSubmit, onReset }: SettingsPanelProps) {
  if (!draft) return <section className="admin-empty"><p>Global settings are not available.</p></section>;
  const setBoolean = (key: keyof AdminGlobalSettings, value: boolean) => onChange({ ...draft, [key]: value });
  const setNumber = (key: keyof AdminGlobalSettings, value: string) => onChange({ ...draft, [key]: Math.max(0, Number(value) || 0) });

  return (
    <form className="admin-settings-layout" onSubmit={onSubmit}>
      <div className="admin-settings-main">
        <section className="admin-panel">
          <PanelHeading eyebrow="ACCESS" title="Account and authentication policy" detail="These controls apply immediately to every client." />
          <SettingToggle title="New registrations" detail="Allow new password and Apple accounts to be created." checked={draft.registrations_enabled} onChange={(value) => setBoolean("registrations_enabled", value)} />
          <SettingToggle title="Sign in with Apple" detail="Allow existing and new users to authenticate through Apple." checked={draft.apple_sign_in_enabled} onChange={(value) => setBoolean("apple_sign_in_enabled", value)} />
          <SettingToggle title="Require authentication for plans" detail="Prevent anonymous clients from generating training plans." checked={draft.require_auth_for_plans} onChange={(value) => setBoolean("require_auth_for_plans", value)} />
          <SettingToggle title="Enforce premium adaptive plans" detail="Reserve rollover and recovery-driven adaptations for premium users." checked={draft.enforce_premium} onChange={(value) => setBoolean("enforce_premium", value)} />
        </section>

        <section className="admin-panel">
          <PanelHeading eyebrow="USAGE POLICY" title="Generation limits and cooldowns" detail="Protect capacity while preserving the premium experience." />
          <div className="admin-number-grid">
            <NumberSetting label="Free daily plans" value={draft.plan_daily_limit_free} onChange={(value) => setNumber("plan_daily_limit_free", value)} />
            <NumberSetting label="Premium daily plans" value={draft.plan_daily_limit_premium} onChange={(value) => setNumber("plan_daily_limit_premium", value)} />
            <NumberSetting label="Anonymous daily plans" value={draft.plan_daily_limit_anonymous} onChange={(value) => setNumber("plan_daily_limit_anonymous", value)} />
            <NumberSetting label="Manual cooldown · minutes" value={draft.manual_plan_cooldown_minutes} onChange={(value) => setNumber("manual_plan_cooldown_minutes", value)} />
            <NumberSetting label="Weekly rollover · hours" value={draft.weekly_rollover_cooldown_hours} onChange={(value) => setNumber("weekly_rollover_cooldown_hours", value)} />
            <NumberSetting label="Recovery adaptation · hours" value={draft.recovery_deviation_cooldown_hours} onChange={(value) => setNumber("recovery_deviation_cooldown_hours", value)} />
          </div>
        </section>
      </div>

      <aside className="admin-settings-aside">
        <section className="admin-panel admin-environment-card">
          <p className="eyebrow">ENVIRONMENT</p><h2>Runtime status</h2>
          <dl><div><dt>AI provider</dt><dd className={stats?.mock_mode ? "warning" : ""}>{stats?.mock_mode ? "Mock responses" : "Claude live"}</dd></div><div><dt>API policy source</dt><dd>Database overrides</dd></div><div><dt>Settings scope</dt><dd>All clients</dd></div></dl>
          {stats?.mock_mode ? <p className="admin-warning"><Icon name="info" />Configure the production Anthropic key to enable live plan generation.</p> : null}
        </section>
        <div className="admin-settings-actions"><button className="button admin-refresh" type="button" onClick={onReset} disabled={saving}>Discard changes</button><button className="button primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save global settings"}</button></div>
      </aside>
    </form>
  );
}

function SettingToggle({ title, detail, checked, onChange }: { title: string; detail: string; checked: boolean; onChange: (value: boolean) => void }) {
  return <label className="admin-setting-toggle"><div><strong>{title}</strong><span>{detail}</span></div><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} /><i /></label>;
}

function NumberSetting({ label, value, onChange }: { label: string; value: number; onChange: (value: string) => void }) {
  return <label className="admin-number-setting"><span>{label}</span><input type="number" min="0" value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));
}

function formatShortDate(value?: string) {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(`${value}T00:00:00`));
}

function humanize(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
